/**
 * useLiveSoap — Suscripción Firestore al DOCUMENTO ÚNICO de nota de la
 * consulta (`consultas/{id}/notas_medicas/actual`).
 *
 * Rediseño NU: el servidor mantiene UN documento con el estado completo de la
 * nota (SOAPE + FHIR con ids estables + alertas + educación + propuestas de la
 * IA sobre secciones protegidas). Este hook ya no consolida ni deduplica —
 * solo mapea el documento a los tipos del portal. Toda la lógica de merge vive
 * en Cloud Functions (functions/src/consultas/notaActual.ts).
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, type Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { SOAPData, FHIRItem, AISuggestion } from '../services/medicalAI';

interface NotaActualDoc {
  estado?: 'borrador' | 'firmada';
  version?: number;
  soap?: { s?: string; o?: string; a?: string; p?: string; e?: string };
  propuestasIA?: Partial<Record<keyof SOAPData, string>>;
  fhir?: Array<Record<string, unknown>>;
  alerts?: string[];
  healthEducation?: string;
  suggestions?: Array<Record<string, unknown>>;
  actualizadoEn?: Timestamp;
}

interface UseLiveSoapResult {
  soap: SOAPData;
  fhir: FHIRItem[];
  alerts: string[];
  healthEducation: string;
  suggestions: AISuggestion[];
  /** Propuestas de la IA para secciones que el médico editó (aceptar/descartar). */
  propuestasIA: Partial<SOAPData>;
  estado: 'borrador' | 'firmada' | null;
  version: number;
  notaCount: number;
  lastUpdate: Date | null;
  /** True hasta que llega la primera respuesta del snapshot */
  loading: boolean;
  error: string | null;
}

/**
 * Strip markdown-style formatting that the AI sometimes introduces.
 */
function stripMarkdown(text: string): string {
  if (!text) return text;
  return text
    .replace(/^\d+\.\s*\*\*(.+?)\*\*/gm, '$1')
    .replace(/^-\s*\*\*(.+?)\*\*/gm, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^#{1,4}\s+/gm, '');
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

function mapFhir(raw?: Array<Record<string, unknown>>): FHIRItem[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((r, idx) => ({
    id: typeof r.id === 'string' ? r.id : `fhir-${idx}`,
    type: ((typeof r.type === 'string' ? r.type : 'condition') as FHIRItem['type']),
    status: typeof r.status === 'string' ? r.status : 'active',
    display: typeof r.display === 'string' ? r.display : '',
    details: str(r.details),
    code: str(r.code),
    dose: str(r.dose),
    presentation: str(r.presentation),
    route: str(r.route),
    frequency: str(r.frequency),
    duration: str(r.duration),
    prescriptionType: ((typeof r.prescriptionType === 'string'
      ? r.prescriptionType
      : undefined) as FHIRItem['prescriptionType']),
    warning: str(r.warning),
    warningLevel: ((typeof r.warningLevel === 'string'
      ? r.warningLevel
      : undefined) as FHIRItem['warningLevel']),
  }));
}

function mapSuggestions(raw?: Array<Record<string, unknown>>): AISuggestion[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .filter((s) => typeof s.message === 'string' && (s.message as string).trim())
    .map((s, i) => ({
      id: typeof s.id === 'string' ? s.id : `ai-sug-${i}`,
      type: (typeof s.type === 'string' ? s.type : 'incomplete') as AISuggestion['type'],
      message: s.message as string,
      section: typeof s.section === 'string' ? (s.section as AISuggestion['section']) : undefined,
      priority: (typeof s.priority === 'string' ? s.priority : 'low') as AISuggestion['priority'],
    }));
}

export function useLiveSoap(consultaId: string | undefined): UseLiveSoapResult {
  const [nota, setNota] = useState<NotaActualDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    if (!consultaId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false);
      return;
    }

    const ref = doc(db, `consultas/${consultaId}/notas_medicas/actual`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setNota(snap.exists() ? (snap.data() as NotaActualDoc) : null);
        setLoading(false);
        if (snap.exists()) setLastUpdate(new Date());
      },
      (err) => {
        console.error('[useLiveSoap] subscription error:', err);
        setError(err.message);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [consultaId]);

  const mapped = useMemo(() => {
    const s = nota?.soap ?? {};
    const soap: SOAPData = {
      s: stripMarkdown(s.s ?? ''),
      o: stripMarkdown(s.o ?? ''),
      a: stripMarkdown(s.a ?? ''),
      p: stripMarkdown(s.p ?? ''),
      e: stripMarkdown(s.e ?? ''),
    };
    const propuestasRaw = nota?.propuestasIA ?? {};
    const propuestasIA: Partial<SOAPData> = {};
    for (const k of ['s', 'o', 'a', 'p', 'e'] as Array<keyof SOAPData>) {
      const v = propuestasRaw[k];
      if (typeof v === 'string' && v.trim()) propuestasIA[k] = stripMarkdown(v);
    }
    return {
      soap,
      propuestasIA,
      fhir: mapFhir(nota?.fhir),
      alerts: (nota?.alerts ?? []).filter((a) => typeof a === 'string' && a.trim()),
      healthEducation: stripMarkdown(nota?.healthEducation ?? ''),
      suggestions: mapSuggestions(nota?.suggestions),
    };
  }, [nota]);

  return {
    soap: mapped.soap,
    fhir: mapped.fhir,
    alerts: mapped.alerts,
    healthEducation: mapped.healthEducation,
    suggestions: mapped.suggestions,
    propuestasIA: mapped.propuestasIA,
    estado: nota?.estado ?? null,
    version: nota?.version ?? 0,
    notaCount: nota ? 1 : 0,
    lastUpdate,
    loading,
    error,
  };
}
